// AdminDashboard.jsx - مع إضافة Quiz Builder وفرع الدراسة
import React, { useState, useEffect } from 'react';
import { Navigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { DashboardLayout } from '../../components/common/DashboardLayout';
import * as XLSX from 'xlsx';
import {
  LayoutDashboard,
  CreditCard,
  Key,
  BookOpen,
  PlayCircle,
  ClipboardEdit,
  MessageSquare,
  Users,
  Bell,
  Shield,
  ArrowLeftRight,
  Plus,
  Trash2,
  CheckCircle,
  XCircle,
  Send,
  RefreshCw,
  MessageCircle,
  ChevronLeft,
  Video,
  Edit3,
  PlusCircle,
  VideoOff,
  Award,
  TrendingUp,
  Clock,
  FileText,
  Lock,
  AlertCircle,
  GraduationCap,
  Search,
  Star,
  FileSpreadsheet
} from 'lucide-react';
import { PaymentRequests } from '../../components/admin/PaymentRequests';
import { CenterCodes } from '../../components/admin/CenterCodes';
import { QuizManagement } from '../../components/admin/QuizManagement';
import { QuizBuilder } from '../../components/admin/QuizBuilder';
import { ShieldSettings } from '../../components/admin/ShieldSettings';
import * as d1Api from '../../services/d1Api';

export const AdminDashboard = () => {
  const auth = useAuth() || {};
  const { user = null, profile = null, signOut = () => { } } = auth;
  const [isDarkMode, setIsDarkMode] = useState(true);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [activeTab, setActiveTab] = useState('dashboard');
  const [courses, setCourses] = useState([]);
  const [lectures, setLectures] = useState([]);
  const [students, setStudents] = useState([]);
  const [pendingStudents, setPendingStudents] = useState([]);
  const [kickedStudents, setKickedStudents] = useState([]);
  const [paymentRequests, setPaymentRequests] = useState([]);
  const [quizzes, setQuizzes] = useState([]);
  const [announcements, setAnnouncements] = useState([]);
  const [pendingPosts, setPendingPosts] = useState([]);
  const [approvedPosts, setApprovedPosts] = useState([]);
  const [replyingToPost, setReplyingToPost] = useState(null);
  const [replyContent, setReplyContent] = useState('');
  const [loading, setLoading] = useState(true);
  const [showAddCourse, setShowAddCourse] = useState(false);
  const [showAddLecture, setShowAddLecture] = useState(false);
  const [showQuizBuilder, setShowQuizBuilder] = useState(false);
  const [showQuizCourseSelect, setShowQuizCourseSelect] = useState(false);
  const [selectedCourseForQuiz, setSelectedCourseForQuiz] = useState(null);

  const [showMessageModal, setShowMessageModal] = useState(false);
  const [messageTarget, setMessageTarget] = useState(null);
  const [messageContent, setMessageContent] = useState('');
  const [studentSearch, setStudentSearch] = useState('');
  const [filterGrade, setFilterGrade] = useState('');
  const [filterBranch, setFilterBranch] = useState('');

  // Subscription modal
  const [showSubModal, setShowSubModal] = useState(false);
  const [selectedStudentSub, setSelectedStudentSub] = useState(null);
  const [studentEnrollments, setStudentEnrollments] = useState([]);
  const [subCourseId, setSubCourseId] = useState('');

  const [selectedCourseImage, setSelectedCourseImage] = useState(null);
  const [selectedLectureImage, setSelectedLectureImage] = useState(null);
  const [isUploading, setIsUploading] = useState(false);

  // Grade change modal state
  const [showGradeModal, setShowGradeModal] = useState(false);
  const [gradeTarget, setGradeTarget] = useState(null);
  const [selectedGrade, setSelectedGrade] = useState('');

  const [newAnnouncement, setNewAnnouncement] = useState({ title: '', content: '' });
  const [loadingLeaderboardId, setLoadingLeaderboardId] = useState(null);

  const [newCourse, setNewCourse] = useState({
    title: '',
    description: '',
    price: 0,
    duration_days: 30,
    grade: '1sec',
    image_url: '',
    status: 'active',
    fixed_expiration_date: ''
  });

  const defaultLectureState = {
    title: '',
    description: '',
    course_id: '',
    duration_minutes: 0,
    order_index: 0,
    is_free: false,
    videos_list: [],
    pdfs_list: [],
    thumbnail_url: '',
    homework_url: '',
    quiz_id: '',
    summary_url: '',
    required_quiz_id: '',
    quiz_ids: [],
    is_quiz_required: false,
    is_preview: false
  };
  const [newLecture, setNewLecture] = useState(defaultLectureState);
  const [editMode, setEditMode] = useState(false);
  const [editingLectureId, setEditingLectureId] = useState(null);

  const isSuperAdmin = profile?.role === 'boss';

  if (!user || !profile) {
    if (loading) return null;
    return <Navigate to="/login" replace />;
  }

  useEffect(() => {
    document.body.classList.toggle('light-mode', !isDarkMode);
  }, [isDarkMode]);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    setLoading(true);
    try {
      const coursesResult = await d1Api.getCourses();
      if (coursesResult.success) setCourses(coursesResult.data || []);

      const lecturesResult = await d1Api.getLectures();
      if (lecturesResult.success) setLectures(lecturesResult.data || []);

      const paymentsResult = await d1Api.getPaymentRequests();
      if (paymentsResult.success) setPaymentRequests(paymentsResult.data || []);

      const allStudentsResult = await d1Api.getProfiles('student');
      if (allStudentsResult.success) {
        const allStudents = allStudentsResult.data || [];
        setPendingStudents(allStudents.filter(s => s.accountStatus === 'pending'));
        setStudents(allStudents.filter(s => s.accountStatus === 'active'));
        setKickedStudents(allStudents.filter(s => s.accountStatus === 'kicked'));
      }

      const quizzesResult = await d1Api.getAllQuizzes();
      if (quizzesResult.success) setQuizzes(quizzesResult.data || []);

      const announcementsResult = await d1Api.getAnnouncements();
      if (announcementsResult.success) setAnnouncements(announcementsResult.data || []);

      const pendingPostsRes = await d1Api.getForumPosts('pending');
      if (pendingPostsRes.success) setPendingPosts(pendingPostsRes.data || []);

      const approvedPostsRes = await d1Api.getForumPosts('approved');
      if (approvedPostsRes.success) setApprovedPosts(approvedPostsRes.data || []);
    } catch (err) {
      console.error('Admin loadData failed:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleApprovePost = async (postId) => {
    const res = await d1Api.updateForumPostStatus(postId, 'approved');
    if (res.success) {
      setPendingPosts(prev => prev.filter(p => p.id !== postId));
      loadData(); // Refresh approved posts too
    } else {
      alert('حدث خطأ: ' + (res.error || 'لم يتم اعتماد البوست'));
    }
  };

  const handleDeleteForumPost = async (postId) => {
    if (!confirm('هل أنت متأكد من حذف هذا البوست؟')) return;
    const res = await d1Api.deleteForumPost(postId);
    if (res.success) {
      setPendingPosts(prev => prev.filter(p => p.id !== postId));
      setApprovedPosts(prev => prev.filter(p => p.id !== postId));
    } else {
      alert('فشل حذف البوست');
    }
  };

  const handleReplyToPost = async (postId) => {
    if (!replyContent.trim()) {
      alert('اكتب الرد أولاً');
      return;
    }
    const res = await d1Api.replyToForumPost(postId, replyContent.trim());
    if (res.success) {
      setReplyingToPost(null);
      setReplyContent('');
      loadData(); // Refresh posts
    } else {
      alert('فشل إرسال الرد');
    }
  };

  const handleSendMessage = async () => {
    if (!messageTarget?.id) return;
    if (!messageContent.trim()) {
      alert('اكتب الرسالة أولاً');
      return;
    }
    const res = await d1Api.sendMessageToStudent(messageTarget.id, messageContent.trim());
    if (res.success) {
      setShowMessageModal(false);
      setMessageTarget(null);
      setMessageContent('');
      alert('✅ تم إرسال الرسالة');
    } else {
      alert('❌ فشل إرسال الرسالة: ' + (res.error || 'Unknown'));
    }
  };

  const formatImgbbUrl = (url) => {
    if (!url) return '';
    let formatted = url.trim();
    if (formatted.includes('ibb.co') && !formatted.includes('i.ibb.co')) {
      const id = formatted.split('ibb.co/')[1]?.split('/')[0];
      if (id) return `https://i.ibb.co/${id}/image.png`;
    }
    return formatted;
  };

  const uploadToImgBB = async (file) => {
    const formData = new FormData();
    formData.append('image', file);
    try {
      const res = await fetch('https://api.imgbb.com/1/upload?key=443ac64e63101e13e6043bf1bd1a77ee', {
        method: 'POST',
        body: formData
      });
      const data = await res.json();
      if (data.success) return data.data.url;
      throw new Error(data.error?.message || 'Upload failed');
    } catch (err) {
      console.error('ImgBB Error:', err);
      return null;
    }
  };

  const handleToggleLeaderboard = async (student) => {
    const action = student.is_leaderboard === 1 ? 'remove' : 'add';
    setLoadingLeaderboardId(student.id);
    try {
      const res = await d1Api.toggleLeaderboard(student.id, action);
      if (res.success) {
        loadData();
      } else {
        alert('فشل تحديث لوحة الشرف');
      }
    } catch (err) {
      alert('خطأ: ' + err.message);
    } finally {
      setLoadingLeaderboardId(null);
    }
  };

  const handleAddPoints = async (student) => {
    const pointsStr = window.prompt(`أدخل عدد النقاط لإضافتها للطالب ${student.firstName}:`);
    if (!pointsStr) return;
    const points = parseInt(pointsStr);
    if (isNaN(points) || points <= 0) return alert('رجاء إدخال رقم صحيح أكبر من صفر');
    const res = await d1Api.addStudentPoints(student.id, points);
    if (res.success) { alert('تمت إضافة النقاط بنجاح'); loadData(); }
    else alert('فشل إضافة النقاط');
  };

  const handleDeductPoints = async (student) => {
    const pointsStr = window.prompt('أدخل عدد النقاط لخصمها من الطالب (اكتب الرقم بالموجب وهو سيتم خصمه):');
    if (!pointsStr) return;
    const points = parseInt(pointsStr);
    if (isNaN(points) || points <= 0) return alert('رجاء إدخال رقم صحيح أكبر من صفر');
    const res = await d1Api.addStudentPoints(student.id, -Math.abs(points));
    if (res.success) { alert('تم خصم النقاط بنجاح'); loadData(); }
    else alert('فشل خصم النقاط');
  };

  const handleToggleForumBlock = async (student) => {
    const isCurrentlyBlocked = student.is_blocked_from_forum === 1;
    const action = isCurrentlyBlocked ? 'إلغاء الحظر' : 'حظر من المنتدى';
    if (!confirm(`${action} للطالب "${student.firstName} ${student.lastName}"؟`)) return;
    const res = await d1Api.toggleForumBlock(student.id, !isCurrentlyBlocked);
    if (res.success) {
      loadData();
    } else {
      alert('فشل تحديث حالة الحظر');
    }
  };

  // Per-student RTL mirror: support flips the buttons for THIS student only
  // when their quality button doesn't respond (their YouTube UI is mirrored).
  const handleToggleMirror = async (student) => {
    const isMirrored = student.ui_mirror === 1;
    const action = isMirrored ? 'إلغاء عكس الأزرار' : 'عكس الأزرار لهذا الطالب';
    if (!confirm(`${action} للطالب "${student.firstName} ${student.lastName}"؟`)) return;
    const res = await d1Api.adminUpdateUser(student.id, { ui_mirror: isMirrored ? 0 : 1 });
    if (res.success) {
      loadData();
    } else {
      alert('فشل تحديث عكس الأزرار');
    }
  };

  const handleAddCourse = async () => {
    if (!newCourse.title || (newCourse.price === '' || newCourse.price === null || newCourse.price === undefined)) return alert('الرجاء ملء جميع الحقول المطلوبة');

    setIsUploading(true);
    let finalImageUrl = newCourse.image_url;

    if (selectedCourseImage) {
      const uploadedUrl = await uploadToImgBB(selectedCourseImage);
      if (uploadedUrl) {
        finalImageUrl = uploadedUrl;
      } else {
        setIsUploading(false);
        return alert('فشل رفع صورة الكورس على ImgBB');
      }
    }

    const payload = {
      ...newCourse,
      price: Number(newCourse.price) || 0,
      image_url: formatImgbbUrl(finalImageUrl),
      fixed_expiration_date: newCourse.fixed_expiration_date || null
    };
    const result = await d1Api.createCourse(payload);

    if (result.success) {
      setShowAddCourse(false);
      setNewCourse({
        title: '',
        description: '',
        price: 0,
        duration_days: 30,
        grade: '1sec',
        image_url: '',
        status: 'active',
        fixed_expiration_date: ''
      });
      setSelectedCourseImage(null);
      loadData();
    } else {
      alert('حدث خطأ: ' + result.error);
    }
    setIsUploading(false);
  };

  const handleAddLecture = async () => {
    if (!newLecture.title || newLecture.videos_list.length === 0 || !newLecture.course_id) {
      alert('يا وحش املأ العناوين وأضف فيديو واحد على الأقل واختر الكورس الأول!');
      return;
    }

    setLoading(true);

    let finalThumbnailUrl = newLecture.thumbnail_url;
    if (selectedLectureImage) {
      setIsUploading(true);
      const uploadedUrl = await uploadToImgBB(selectedLectureImage);
      if (uploadedUrl) {
        finalThumbnailUrl = uploadedUrl;
      } else {
        setIsUploading(false);
        setLoading(false);
        return alert('فشل رفع صورة المحاضرة على ImgBB');
      }
      setIsUploading(false);
    }

    const allowedKeys = ['title', 'description', 'course_id', 'order_index', 'is_free', 'duration_minutes', 'thumbnail_url', 'videos_list', 'pdfs_list', 'quiz_id', 'quiz_ids', 'summary_url', 'external_link', 'homework_url', 'required_quiz_id', 'is_quiz_required', 'is_preview'];
    const filteredData = Object.fromEntries(Object.entries(newLecture).filter(([key]) => allowedKeys.includes(key)));
    filteredData.duration_minutes = Number(filteredData.duration_minutes) || 0;
    filteredData.order_index = Number(filteredData.order_index) || 0;
    filteredData.is_free = filteredData.is_free ? 1 : 0;
    filteredData.is_quiz_required = filteredData.is_quiz_required ? 1 : 0;
    filteredData.is_preview = filteredData.is_preview ? 1 : 0;

    console.log('--- DEBUG START (ADD) ---');
    console.log('Original State:', newLecture);
    console.log('Filtered Payload:', filteredData);
    console.log('--- DEBUG END (ADD) ---');

    filteredData.thumbnail_url = formatImgbbUrl(finalThumbnailUrl);
    filteredData.summary_url = formatImgbbUrl(filteredData.summary_url);

    const result = await d1Api.createLecture(filteredData);

    if (result.success) {
      setShowAddLecture(false);
      setNewLecture({
        title: '', description: '', course_id: '',
        duration_minutes: 0, is_free: false, order_index: 0,
        videos_list: [], pdfs_list: [],
        summary_url: '', thumbnail_url: '', homework_url: '', external_link: '', quiz_id: ''
      });
      await loadData();
      alert('✅ المحاضرة نزلت بنجاح!');
      setTimeout(() => { window.location.reload(); }, 1500);
    } else {
      alert('❌ حدث خطأ: ' + result.error);
    }
    setLoading(false);
  };

  const handleSaveLecture = async () => {
    if (editMode && editingLectureId) {
      // Edit mode: update existing lecture
      if (!newLecture.title || newLecture.videos_list.length === 0 || !newLecture.course_id) {
        alert('يا وحش املأ العناوين وأضف فيديو واحد على الأقل واختر الكورس الأول!');
        return;
      }

      setLoading(true);

      let finalThumbnailUrl = newLecture.thumbnail_url;
      if (selectedLectureImage) {
        setIsUploading(true);
        const uploadedUrl = await uploadToImgBB(selectedLectureImage);
        if (uploadedUrl) {
          finalThumbnailUrl = uploadedUrl;
        } else {
          setIsUploading(false);
          setLoading(false);
          return alert('فشل رفع صورة المحاضرة على ImgBB');
        }
        setIsUploading(false);
      }

      const allowedKeys = ['title', 'description', 'course_id', 'order_index', 'is_free', 'duration_minutes', 'thumbnail_url', 'videos_list', 'pdfs_list', 'quiz_id', 'quiz_ids', 'summary_url', 'external_link', 'homework_url', 'required_quiz_id', 'is_quiz_required', 'is_preview'];
      const filteredData = Object.fromEntries(Object.entries(newLecture).filter(([key]) => allowedKeys.includes(key)));
      filteredData.duration_minutes = Number(filteredData.duration_minutes) || 0;
      filteredData.order_index = Number(filteredData.order_index) || 0;
      filteredData.is_free = filteredData.is_free ? 1 : 0;
      filteredData.is_quiz_required = filteredData.is_quiz_required ? 1 : 0;
      filteredData.is_preview = filteredData.is_preview ? 1 : 0;

      filteredData.thumbnail_url = formatImgbbUrl(finalThumbnailUrl);
      filteredData.summary_url = formatImgbbUrl(filteredData.summary_url);

      const result = await d1Api.updateLecture(editingLectureId, filteredData);

      if (result.success) {
        setShowAddLecture(false);
        setEditMode(false);
        setEditingLectureId(null);
        setNewLecture({
          title: '', description: '', course_id: '',
          duration_minutes: 0, is_free: false, order_index: 0,
          videos_list: [], pdfs_list: [],
          summary_url: '', thumbnail_url: '', homework_url: '', external_link: '', quiz_id: '', quiz_ids: []
        });
        await loadData();
        alert('✅ تم تحديث المحاضرة بنجاح!');
        setTimeout(() => { window.location.reload(); }, 1500);
      } else {
        alert('❌ حدث خطأ: ' + result.error);
      }
      setLoading(false);
    } else {
      // Create mode
      await handleAddLecture();
    }
  };


  const handleDeleteCourse = async (courseId) => {
    if (confirm('هل أنت متأكد من حذف هذا الكورس؟')) {
      const result = await d1Api.deleteCourse(courseId);
      if (result.success) loadData();
      else alert('حدث خطأ: ' + result.error);
    }
  };

  const handleDeleteLecture = async (lectureId) => {
    if (confirm('هل أنت متأكد من حذف هذه المحاضرة؟')) {
      const result = await d1Api.deleteLecture(lectureId);
      if (result.success) loadData();
      else alert('حدث خطأ: ' + result.error);
    }
  };

  const handleCreateAnnouncement = async (e) => {
    e.preventDefault();
    if (!newAnnouncement.title || !newAnnouncement.content) {
      alert('الرجاء كتابة العنوان والمحتوى');
      return;
    }
    const result = await d1Api.createAnnouncement(newAnnouncement);
    if (result.success) {
      setNewAnnouncement({ title: '', content: '' });
      loadData();
    } else {
      alert('حدث خطأ أثناء إضافة الإعلان');
    }
  };

  const handleDeleteAnnouncement = async (id) => {
    if (confirm('هل ترغب حقاً في حذف هذا الإعلان؟')) {
      const result = await d1Api.deleteAnnouncement(id);
      if (result.success) loadData();
    }
  };

  const handleAccept = async (student) => {
    if (!confirm(`قبول الطالب "${student.firstName} ${student.lastName}"؟`)) return;
    const result = await d1Api.updateUserStatus(student.id, 'active');
    if (result.success) {
      setPendingStudents(prev => prev.filter(s => s.id !== student.id));
      setStudents(prev => [...prev, { ...student, accountStatus: 'active' }]);
    } else {
      alert('حدث خطأ: ' + (result.error || 'لم يتم القبول'));
    }
  };

  const handleReject = async (student) => {
    if (!confirm(`رفض وحذف الطالب "${student.firstName} ${student.lastName}" نهائياً؟`)) return;
    const result = await d1Api.deleteUser(student.id);
    if (result.success) {
      setPendingStudents(prev => prev.filter(s => s.id !== student.id));
    } else {
      alert('حدث خطأ: ' + (result.error || 'لم يتم الحذف'));
    }
  };

  const handleKick = async (student) => {
    if (!confirm(`طرد الطالب "${student.firstName} ${student.lastName}" من المنصة؟`)) return;
    const result = await d1Api.kickUser(student.id);
    if (result.success) {
      setStudents(prev => prev.filter(s => s.id !== student.id));
      setKickedStudents(prev => [...prev, { ...student, accountStatus: 'kicked' }]);
    } else {
      alert('حدث خطأ: ' + (result.error || 'لم يتم الطرد'));
    }
  };

  const handleRestore = async (student) => {
    if (!confirm(`استعادة الطالب "${student.firstName} ${student.lastName}" وإعادته للمنصة؟`)) return;
    const result = await d1Api.updateUserStatus(student.id, 'active');
    if (result.success) {
      setKickedStudents(prev => prev.filter(s => s.id !== student.id));
      setStudents(prev => [...prev, { ...student, accountStatus: 'active' }]);
    } else {
      alert('حدث خطأ: ' + (result.error || 'لم تتم الاستعادة'));
    }
  };

  const handleResetDevice = async (student) => {
    if (confirm(`هل أنت متأكد من فك ارتباط جهاز الطالب ${student.firstName}؟`)) {
      const res = await d1Api.resetDeviceBinding(student.id);
      if (res.success) {
        alert('تم فك ارتباط الجهاز بنجاح. يمكن للطالب الآن تسجيل الدخول من جهاز جديد.');
        loadData();
      } else {
        alert('حدث خطأ: ' + (res.error || 'Unknown'));
      }
    }
  };

  const handleExportStudentsToExcel = () => {
    // Get all active students and sort by grade level
    const allStudents = [...students];

    // Sort by grade: 1sec (First Secondary), 2sec (Second Secondary), 3sec (Third Secondary)
    const gradeOrder = { '1sec': 1, '2sec': 2, '3sec': 3 };
    allStudents.sort((a, b) => gradeOrder[a.grade] - gradeOrder[b.grade]);

    // Prepare data for CSV with Arabic headers
    const csvData = allStudents.map(student => ({
      'اسم الطالب': [student.firstName, student.middleName, student.lastName].filter(Boolean).join(' '),
      'رقم هاتف الطالب': student.phone || '',
      'رقم ولي الأمر': student.father_phone || student.mother_phone || '',
      'الصف الدراسي': student.grade === '1sec' ? 'الصف الأول الثانوي' :
                     student.grade === '2sec' ? 'الصف الثاني الثانوي' :
                     student.grade === '3sec' ? 'الصف الثالث الثانوي' : '',
      'الشعبة': student.study_branch === 'scientific' ? 'علمي' : 'أدبي',
      'المحافظة': student.governorate || '',
      'المدرسة': student.school || ''
    }));

    // Create worksheet from data
    const ws = XLSX.utils.json_to_sheet(csvData);

    // Convert worksheet to CSV string
    const csvOutput = XLSX.utils.sheet_to_csv(ws);

    // CRITICAL: Prepend UTF-8 BOM to fix Arabic characters in Excel
    // Without BOM, Excel displays Arabic as gibberish
    const blob = new Blob(["\uFEFF" + csvOutput], { type: "text/csv;charset=utf-8;" });

    // Create temporary download link
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = 'Sabeel_Students_List.csv';
    
    // Trigger download
    document.body.appendChild(link);
    link.click();
    
    // Cleanup
    document.body.removeChild(link);
    URL.revokeObjectURL(link.href);
  };

  const handleManualSubscription = async (student) => {
    setSelectedStudentSub(student);
    setShowSubModal(true);
    setSubCourseId('');
    try {
      const res = await d1Api.getEnrolledCourses(student.id);
      setStudentEnrollments(res.success ? (res.data || []) : []);
    } catch {
      setStudentEnrollments([]);
    }
  };

  const handleRevokeSub = async (enrollmentId) => {
    if (!confirm('هل أنت متأكد من سحب هذا الاشتراك؟')) return;
    const res = await d1Api.revokeEnrollment(enrollmentId);
    if (res.success) {
      setStudentEnrollments(prev => prev.filter(e => e.enrollment_id !== enrollmentId));
    } else {
      alert('حدث خطأ: ' + (res.error || 'لم يتم السحب'));
    }
  };

  const handleAddSub = async () => {
    if (!subCourseId || !selectedStudentSub) return;
    const res = await d1Api.enrollInCourse(subCourseId, selectedStudentSub.id);
    if (res.success) {
      const updated = await d1Api.getEnrolledCourses(selectedStudentSub.id);
      setStudentEnrollments(updated.success ? (updated.data || []) : studentEnrollments);
      setSubCourseId('');
    } else {
      alert('حدث خطأ: ' + (res.error || 'لم يتم التفعيل'));
    }
  };

  // Handle grade change
  const handleChangeGrade = async (student, newGrade) => {
    if (!newGrade) return;
    const res = await d1Api.adminUpdateUser(student.id, { grade: newGrade });
    if (res.success) {
      // Update local state for active students
      setStudents(prev => prev.map(s => s.id === student.id ? { ...s, grade: newGrade } : s));
      // Also update pending and kicked lists
      setPendingStudents(prev => prev.map(s => s.id === student.id ? { ...s, grade: newGrade } : s));
      setKickedStudents(prev => prev.map(s => s.id === student.id ? { ...s, grade: newGrade } : s));
      alert('✅ تم تغيير الصف بنجاح');
      setShowGradeModal(false);
      setGradeTarget(null);
      setSelectedGrade('');
    } else {
      alert('❌ فشل تغيير الصف: ' + (res.error || 'Unknown'));
    }
  };

  // Handle trial toggle
  const handleToggleTrial = async (student) => {
    const newTrialValue = student.is_trial === 1 ? 0 : 1;
    const res = await d1Api.adminUpdateUser(student.id, { is_trial: newTrialValue });
    if (res.success) {
      setStudents(prev => prev.map(s => s.id === student.id ? { ...s, is_trial: newTrialValue } : s));
      setPendingStudents(prev => prev.map(s => s.id === student.id ? { ...s, is_trial: newTrialValue } : s));
      setKickedStudents(prev => prev.map(s => s.id === student.id ? { ...s, is_trial: newTrialValue } : s));
    } else {
      alert('❌ فشل تغيير نوع الحساب: ' + (res.error || 'Unknown'));
    }
  };

  const stats = {
    students: students.length,
    pendingStudents: pendingStudents.length,
    courses: courses.length,
    lectures: lectures.length,
    quizzes: quizzes.length,
    pendingPayments: paymentRequests.filter(r => (r.requestStatus || r.status) === 'pending').length
  };

  const menuItems = [
    { id: 'dashboard', label: 'الرئيسية', Icon: LayoutDashboard },
    { id: 'payments', label: 'طلبات الدفع', Icon: CreditCard, badge: stats.pendingPayments },
    { id: 'codes', label: 'أكواد السنتر', Icon: Key },
    { id: 'courses', label: 'الكورسات', Icon: BookOpen, count: courses.length },
    { id: 'lectures', label: 'المحاضرات', Icon: PlayCircle, count: lectures.length },
    { id: 'quizzes', label: 'الامتحانات', Icon: ClipboardEdit, count: quizzes.length },
    { id: 'forum', label: 'المنتدى', Icon: MessageSquare, badge: pendingPosts.length },
    { id: 'students', label: 'إدارة الطلاب', Icon: Users, count: students.length + pendingStudents.length, badge: kickedStudents.length || undefined },
    { id: 'announcements', label: 'الإعلانات', Icon: Bell },
    { id: 'shield', label: 'حماية الفيديو', Icon: Shield },
  ];

  if (loading) {
    return (
      <div className="min-h-screen bg-black flex items-center justify-center">
        <div className="text-center">
          <div className="w-16 h-16 border-4 border-white border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-gray-400">جاري التحميل...</p>
        </div>
      </div>
    );
  }

  return (
    <DashboardLayout
      menuItems={menuItems}
      activeTab={activeTab}
      setActiveTab={setActiveTab}
      isDarkMode={isDarkMode}
      setIsDarkMode={setIsDarkMode}
      signOut={signOut}
      profile={profile}
      sidebarOpen={sidebarOpen}
      setSidebarOpen={setSidebarOpen}
    >
      {activeTab === 'dashboard' && (
        <div className="space-y-6">
          <div className="glass-panel p-8">
            <h1 className="text-3xl font-black mb-2">
              مرحباً {profile?.firstName || 'مدير'} 👋
            </h1>
            <p className="text-slate-400">
              عندك {stats.pendingPayments} طلب دفع pending و{students.length} طالب مسجل
            </p>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
            <div className="glass-panel p-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-xl bg-white/10 flex items-center justify-center">
                  <Users size={20} className="text-white" />
                </div>
                <div>
                  <div className="text-2xl font-bold">{stats.students}</div>
                  <div className="text-xs text-slate-400">طلاب</div>
                </div>
              </div>
            </div>

            <div className="glass-panel p-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-xl bg-white/10 flex items-center justify-center">
                  <BookOpen size={20} className="text-white" />
                </div>
                <div>
                  <div className="text-2xl font-bold">{stats.courses}</div>
                  <div className="text-xs text-slate-400">كورسات</div>
                </div>
              </div>
            </div>

            <div className="glass-panel p-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-xl bg-white/10 flex items-center justify-center">
                  <Video size={20} className="text-white" />
                </div>
                <div>
                  <div className="text-2xl font-bold">{stats.lectures}</div>
                  <div className="text-xs text-slate-400">محاضرات</div>
                </div>
              </div>
            </div>

            <div className="glass-panel p-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-xl bg-white/10 flex items-center justify-center">
                  <ClipboardEdit size={20} className="text-white" />
                </div>
                <div>
                  <div className="text-2xl font-bold">{stats.quizzes}</div>
                  <div className="text-xs text-slate-400">امتحانات</div>
                </div>
              </div>
            </div>

            <div className="glass-panel p-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-xl bg-white/10 flex items-center justify-center">
                  <CreditCard size={20} className="text-white" />
                </div>
                <div>
                  <div className="text-2xl font-bold">{stats.pendingPayments}</div>
                  <div className="text-xs text-slate-400">طلبات معلقة</div>
                </div>
              </div>
            </div>
          </div>

          <div className="glass-panel p-6">
            <h3 className="font-bold mb-4">⚡ إجراءات سريعة</h3>
            <div className="grid md:grid-cols-3 gap-4">
              <button
                onClick={() => setShowAddCourse(true)}
                className="bg-white/5 hover:bg-white/20 hover:text-white p-4 rounded-xl font-bold transition text-center"
              >
                <PlusCircle size={24} className="mx-auto mb-2" />
                إضافة كورس جديد
              </button>

              <button
                onClick={() => setShowAddLecture(true)}
                className="bg-white/5 hover:bg-white/20 hover:text-white p-4 rounded-xl font-bold transition text-center"
              >
                <Video size={24} className="mx-auto mb-2" />
                رفع محاضرة جديدة
              </button>

              <button
                onClick={() => {
                  setActiveTab('courses');
                }}
                className="bg-white/5 hover:bg-white/20 hover:text-white p-4 rounded-xl font-bold transition text-center"
              >
                <ClipboardEdit size={24} className="mx-auto mb-2" />
                إنشاء امتحان
              </button>
            </div>
          </div>
        </div>
      )}

      {activeTab === 'payments' && <PaymentRequests onUpdate={loadData} />}

      {activeTab === 'codes' && <CenterCodes courses={courses} />}

      {activeTab === 'quizzes' && (
        <div className="space-y-6">
          <div className="flex items-center justify-between">
            <h2 className="text-2xl font-bold">📝 إدارة الامتحانات</h2>
            <button
              onClick={() => setShowQuizCourseSelect(true)}
              className="btn-main px-6 py-3 rounded-xl font-bold flex items-center gap-2"
            >
              <Plus size={18} />
              إضافة امتحان جديد
            </button>
          </div>
          <QuizManagement />
        </div>
      )}

      {activeTab === 'courses' && (
        <div className="space-y-6">
          <div className="flex items-center justify-between">
            <h2 className="text-2xl font-bold">📚 إدارة الكورسات</h2>
            <button
              onClick={() => setShowAddCourse(true)}
              className="btn-main px-6 py-3 rounded-xl font-bold flex items-center gap-2"
            >
              <Plus size={18} />
              إضافة كورس
            </button>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {courses.map((course) => (
              <div key={course.id} className="glass-panel overflow-hidden group">
                <div className="h-40 bg-gradient-to-br from-slate-800 to-slate-900 relative">
                  {course.image_url ? (
                    <img
                      src={course.image_url}
                      alt={course.title}
                      className="w-full h-full object-cover opacity-70"
                    />
                  ) : (
                    <div className="absolute inset-0 flex items-center justify-center">
                      <BookOpen size={40} className="opacity-30" />
                    </div>
                  )}
                  <div className="absolute inset-0 bg-gradient-to-t from-black/80 to-transparent"></div>
                  <div className="absolute bottom-4 right-4">
                    <h3 className="font-bold text-lg">{course.title}</h3>
                  </div>
                  <span className="absolute top-4 left-4 bg-brand-red/90 text-white px-2 py-1 rounded text-xs font-bold">
                    {course.price} ج.م
                  </span>
                  <span className={`absolute top-4 right-4 px-2 py-1 rounded text-xs font-bold ${
                    (course.status || 'active') === 'active' ? 'bg-green-600/90 text-white' :
                    (course.status || 'active') === 'locked' ? 'bg-yellow-600/90 text-white' :
                    'bg-slate-700/90 text-slate-300'
                  }`}>
                    {(course.status || 'active') === 'active' ? '✅ منشور' :
                     (course.status || 'active') === 'locked' ? '🔒 مقفول' : '👁️ مخفي'}
                  </span>
                </div>
                <div className="p-4">
                  <p className="text-sm text-slate-400 line-clamp-2 mb-3">{course.description}</p>

                  {/* Fixed Expiration Date Display & Edit */}
                  <div className="mb-3">
                    <label className="text-xs text-slate-500 block mb-1">📅 تاريخ انتهاء ثابت:</label>
                    <div className="flex items-center gap-2">
                      <input
                        type="date"
                        value={course.fixed_expiration_date ? new Date(Number(course.fixed_expiration_date)).toISOString().split('T')[0] : ''}
                        onChange={async (e) => {
                          const newVal = e.target.value || null;
                          const res = await d1Api.updateCourse(course.id, { fixed_expiration_date: newVal });
                          if (res.success) {
                            setCourses(prev => prev.map(c => c.id === course.id ? { ...c, fixed_expiration_date: newVal ? Date.parse(newVal) : null } : c));
                          } else {
                            alert('فشل تحديث تاريخ الانتهاء: ' + (res.error || ''));
                          }
                        }}
                        className="flex-1 p-2 bg-white/5 border border-white/10 rounded-lg text-white text-sm"
                      />
                      {course.fixed_expiration_date && (
                        <button
                          onClick={async () => {
                            const res = await d1Api.updateCourse(course.id, { fixed_expiration_date: null });
                            if (res.success) {
                              setCourses(prev => prev.map(c => c.id === course.id ? { ...c, fixed_expiration_date: null } : c));
                            }
                          }}
                          className="text-red-400 hover:text-red-300 text-xs bg-red-500/10 px-2 py-1.5 rounded-lg transition"
                          title="إزالة تاريخ الانتهاء الثابت"
                        >
                          ✕
                        </button>
                      )}
                    </div>
                    {course.fixed_expiration_date && (
                      <p className={`text-xs mt-1 font-bold ${
                        Number(course.fixed_expiration_date) <= Date.now() ? 'text-red-400' : 'text-emerald-400'
                      }`}>
                        {Number(course.fixed_expiration_date) <= Date.now()
                          ? '⚠️ منتهي الصلاحية — الطلاب لا يمكنهم الوصول'
                          : `✅ ساري حتى ${new Date(Number(course.fixed_expiration_date)).toLocaleDateString('ar-EG')}`
                        }
                      </p>
                    )}
                    {!course.fixed_expiration_date && (
                      <p className="text-xs mt-1 text-slate-500">لم يتم تعيين تاريخ — يُستخدم الانتهاء الفردي للاشتراك</p>
                    )}
                  </div>

                  <div className="mb-3">
                    <label className="text-xs text-slate-500 block mb-1">تغيير الحالة:</label>
                    <select
                      value={course.status || 'active'}
                      onChange={async (e) => {
                        const newStatus = e.target.value;
                        const res = await d1Api.updateCourse(course.id, { status: newStatus });
                        if (res.success) {
                          setCourses(prev => prev.map(c => c.id === course.id ? { ...c, status: newStatus } : c));
                        } else {
                          alert('فشل تحديث الحالة: ' + (res.error || ''));
                        }
                      }}
                      className="w-full p-2 bg-white/5 border border-white/10 rounded-lg text-white text-sm"
                    >
                      <option value="active">✅ منشور - متاح للجميع</option>
                      <option value="locked">🔒 مقفول - يظهر بعلامة قفل</option>
                      <option value="hidden">👁️ مخفي - لا يظهر للطلاب</option>
                    </select>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-xs text-slate-400">
                      {course.grade === '1sec' ? 'أولى ثانوي' : course.grade === '2sec' ? 'تانية ثانوي' : 'تالتة ثانوي'}
                    </span>
                    <div className="flex gap-2">
                      <button
                        onClick={() => {
                          setSelectedCourseForQuiz(course);
                          setShowQuizBuilder(true);
                        }}
                        className="text-purple-400 hover:text-purple-300 p-2 rounded-lg transition"
                        title="إضافة امتحان"
                      >
                        <ClipboardEdit size={16} />
                      </button>
                      <button
                        onClick={() => handleDeleteCourse(course.id)}
                        className="text-red-400 hover:text-red-300 p-2 rounded-lg transition"
                      >
                        <Trash2 size={16} />
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {activeTab === 'lectures' && (
        <div className="space-y-6">
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-2xl font-bold">🎥 إدارة المحاضرات</h2>
            <button
              onClick={() => {
                setNewLecture(defaultLectureState);
                setShowAddLecture(true);
              }}
              className="btn-main px-6 py-3 rounded-xl font-bold flex items-center gap-2"
            >
              <Plus size={18} />
              إضافة محاضرة
            </button>
          </div>

          {lectures.length === 0 && (
            <div className="glass-panel p-12 text-center">
              <VideoOff size={48} className="mx-auto mb-4 opacity-60" />
              <h3 className="text-xl font-bold mb-2">لا توجد محاضرات</h3>
              <p className="text-slate-400">تم تحميل الصفحة بنجاح، لكن لم يتم العثور على محاضرات في قاعدة البيانات.</p>
            </div>
          )}

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {lectures.map((lecture) => (
              <div key={lecture.id} className="glass-panel overflow-hidden group">
                <div className="h-40 bg-gradient-to-br from-slate-800 to-slate-900 relative overflow-hidden flex items-center justify-center">
                  {lecture.thumbnail_url ? (
                    <img
                      src={lecture.thumbnail_url}
                      alt={lecture.title}
                      className="w-full h-full object-cover opacity-80 group-hover:opacity-100 transition-opacity duration-300"
                    />
                  ) : (
                    <Video size={48} className="opacity-30" />
                  )}
                  <div className="absolute inset-0 bg-gradient-to-t from-black/70 to-transparent pointer-events-none"></div>
                  {lecture.is_free && (
                    <span className="absolute top-4 right-4 bg-green-600 text-white px-2 py-1 rounded text-xs font-bold z-10">
                      مجاني
                    </span>
                  )}
                </div>
                <div className="p-4">
                  <h3 className="font-bold mb-2">{lecture.title}</h3>
                  <p className="text-xs text-slate-400 mb-3 line-clamp-2">{lecture.description}</p>
                  <div className="flex items-center justify-between mt-3">
                    <span className="text-xs text-slate-400">
                      {lecture.courses?.title || 'بدون كورس'}
                    </span>
                    <div className="flex gap-2">
                      <button
                        onClick={() => {
                          const lectureData = {
                            title: lecture.title || '',
                            description: lecture.description || '',
                            course_id: lecture.course_id || '',
                            duration_minutes: Number(lecture.duration_minutes) || 0,
                            order_index: Number(lecture.order_index) || 0,
                            is_free: Number(lecture.is_free) === 1,
                            videos_list: lecture.videos_list ? (typeof lecture.videos_list === 'string' ? JSON.parse(lecture.videos_list) : lecture.videos_list) : [],
                            pdfs_list: lecture.pdfs_list ? (typeof lecture.pdfs_list === 'string' ? JSON.parse(lecture.pdfs_list) : lecture.pdfs_list) : [],
                            thumbnail_url: lecture.thumbnail_url || '',
                            homework_url: lecture.homework_url || '',
                            quiz_id: lecture.quiz_id || '',
                            quiz_ids: lecture.quiz_ids ? (typeof lecture.quiz_ids === 'string' ? JSON.parse(lecture.quiz_ids) : lecture.quiz_ids) : [],
                            summary_url: lecture.summary_url || '',
                            external_link: lecture.external_link || '',
                            required_quiz_id: lecture.required_quiz_id || '',
                            is_quiz_required: Number(lecture.is_quiz_required) === 1,
                            is_preview: Number(lecture.is_preview) === 1
                          };
                          console.log('Editing lecture:', lectureData);
                          setNewLecture(lectureData);
                          setEditMode(true);
                          setEditingLectureId(lecture.id);
                          setShowAddLecture(true);
                        }}
                        className="text-blue-400 hover:text-blue-300 p-2 rounded-lg transition"
                        title="تعديل"
                      >
                        <Edit3 size={16} />
                      </button>
                      <button
                        onClick={() => handleDeleteLecture(lecture.id)}
                        className="text-red-400 hover:text-red-300 p-2 rounded-lg transition"
                      >
                        <Trash2 size={16} />
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {activeTab === 'forum' && (
        <div className="space-y-6">
          <div className="flex items-center justify-between">
            <h2 className="text-2xl font-bold">💬 إدارة المنتدى</h2>
            <button
              onClick={loadData}
              className="bg-white/10 hover:bg-white/20 px-4 py-2 rounded-xl font-bold transition"
            >
              تحديث
            </button>
          </div>

          {/* Pending Posts */}
          <div>
            <h3 className="text-xl font-bold mb-4 text-yellow-400">🟡 بوستات قيد المراجعة ({pendingPosts.length})</h3>
            {pendingPosts.length === 0 ? (
              <div className="glass-panel p-12 text-center">
                <MessageCircle size={48} className="mx-auto mb-4 opacity-60" />
                <h3 className="text-xl font-bold mb-2">لا يوجد بوستات معلقة</h3>
                <p className="text-slate-400">أي بوست جديد من الطلاب سيظهر هنا للمراجعة.</p>
              </div>
            ) : (
              <div className="space-y-4">
                {pendingPosts.map((p) => (
                  <div key={p.id} className="glass-panel p-6 relative">
                    <button
                      onClick={() => handleDeleteForumPost(p.id)}
                      className="absolute top-4 left-4 text-red-400 hover:text-red-300 bg-red-400/10 p-2 rounded-lg transition"
                      title="حذف البوست"
                    >
                      <Trash2 size={16} />
                    </button>
                    <div className="flex items-center justify-between gap-4">
                      <div className="min-w-0 flex-1">
                        <div className="text-sm text-slate-400">
                          {p.firstName || 'طالب'} {p.lastName || ''} • {new Date(p.created_at || Date.now()).toLocaleString('ar-EG')}
                        </div>
                        <p className="mt-3 text-white whitespace-pre-wrap break-words">{p.content}</p>
                      </div>
                      <div className="flex gap-2">
                        <button
                          onClick={() => {
                            setReplyingToPost(p.id);
                            setReplyContent(p.admin_reply || '');
                          }}
                          className="bg-blue-500/20 text-blue-400 px-4 py-2 rounded-xl font-bold hover:bg-blue-500 hover:text-white transition"
                        >
                          رد الإدارة
                        </button>
                        <button
                          onClick={() => handleApprovePost(p.id)}
                          className="btn-main px-4 py-2 rounded-xl font-bold whitespace-nowrap"
                        >
                          اعتماد
                        </button>
                      </div>
                    </div>
                    {replyingToPost === p.id && (
                      <div className="mt-4 p-4 bg-blue-500/10 border border-blue-500/30 rounded-xl">
                        <textarea
                          value={replyContent}
                          onChange={(e) => setReplyContent(e.target.value)}
                          placeholder="اكتب رد الإدارة هنا..."
                          className="w-full p-3 bg-white/5 border border-white/10 rounded-xl text-white h-24"
                        />
                        <div className="flex gap-2 mt-2">
                          <button
                            onClick={() => handleReplyToPost(p.id)}
                            className="btn-main px-4 py-2 rounded-xl font-bold"
                          >
                            إرسال الرد
                          </button>
                          <button
                            onClick={() => { setReplyingToPost(null); setReplyContent(''); }}
                            className="bg-white/10 hover:bg-white/20 px-4 py-2 rounded-xl font-bold"
                          >
                            إلغاء
                          </button>
                        </div>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Approved Posts */}
          <div>
            <h3 className="text-xl font-bold mb-4 text-green-400">🟢 البوستات المعتمدة ({approvedPosts.length})</h3>
            {approvedPosts.length === 0 ? (
              <div className="glass-panel p-8 text-center text-slate-400">
                لا توجد بوستات معتمدة بعد.
              </div>
            ) : (
              <div className="space-y-4">
                {approvedPosts.map((p) => (
                  <div key={p.id} className="glass-panel p-6 relative">
                    <button
                      onClick={() => handleDeleteForumPost(p.id)}
                      className="absolute top-4 left-4 text-red-400 hover:text-red-300 bg-red-400/10 p-2 rounded-lg transition"
                      title="حذف البوست"
                    >
                      <Trash2 size={16} />
                    </button>
                    <div className="min-w-0">
                      <div className="text-sm text-slate-400">
                        {p.firstName || 'طالب'} {p.lastName || ''} • {new Date(p.created_at || Date.now()).toLocaleString('ar-EG')}
                      </div>
                      <p className="mt-3 text-white whitespace-pre-wrap break-words">{p.content}</p>
                      {p.admin_reply && (
                        <div className="mt-3 p-3 bg-blue-500/10 border border-blue-500/30 rounded-xl">
                          <span className="text-xs font-bold text-blue-400">رد المستر:</span>
                          <p className="text-slate-300 mt-1">{p.admin_reply}</p>
                        </div>
                      )}
                    </div>
                    {replyingToPost === p.id ? (
                      <div className="mt-4 p-4 bg-blue-500/10 border border-blue-500/30 rounded-xl">
                        <textarea
                          value={replyContent}
                          onChange={(e) => setReplyContent(e.target.value)}
                          placeholder="اكتب رد الإدارة هنا..."
                          className="w-full p-3 bg-white/5 border border-white/10 rounded-xl text-white h-24"
                        />
                        <div className="flex gap-2 mt-2">
                          <button
                            onClick={() => handleReplyToPost(p.id)}
                            className="btn-main px-4 py-2 rounded-xl font-bold"
                          >
                            إرسال الرد
                          </button>
                          <button
                            onClick={() => { setReplyingToPost(null); setReplyContent(''); }}
                            className="bg-white/10 hover:bg-white/20 px-4 py-2 rounded-xl font-bold"
                          >
                            إلغاء
                          </button>
                        </div>
                      </div>
                    ) : (
                      <button
                        onClick={() => {
                          setReplyingToPost(p.id);
                          setReplyContent(p.admin_reply || '');
                        }}
                        className="mt-3 bg-blue-500/20 text-blue-400 px-4 py-2 rounded-xl font-bold hover:bg-blue-500 hover:text-white transition"
                      >
                        {p.admin_reply ? 'تعديل الرد' : 'رد الإدارة'}
                      </button>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}

      {activeTab === 'students' && (
        <div className="space-y-6">
          <h2 className="text-2xl font-bold mb-4 flex items-center gap-2">
            <Users size={24} />
            إدارة الطلاب
          </h2>

          {/* Search & Filters */}
          <div className="flex flex-wrap gap-3">
            <div className="relative flex-1 min-w-[250px]">
              <Search size={18} className="absolute right-4 top-1/2 -translate-y-1/2 text-slate-500" />
              <input
                type="text"
                placeholder="ابحث باسم الطالب أو رقم الهاتف أو المحافظة أو المدرسة..."
                value={studentSearch}
                onChange={(e) => setStudentSearch(e.target.value)}
                className="w-full pr-12 pl-4 py-3 bg-white/5 border border-white/10 rounded-xl text-white placeholder-slate-500 focus:border-brand-gold outline-none"
              />
            </div>
            <select
              value={filterGrade}
              onChange={(e) => setFilterGrade(e.target.value)}
              className="px-4 py-3 bg-white/5 border border-white/10 rounded-xl text-white min-w-[140px]"
            >
              <option value="">الكل</option>
              <option value="1sec">أولى ثانوي</option>
              <option value="2sec">تانية ثانوي</option>
              <option value="3sec">تالتة ثانوي</option>
            </select>
            <select
              value={filterBranch}
              onChange={(e) => setFilterBranch(e.target.value)}
              className="px-4 py-3 bg-white/5 border border-white/10 rounded-xl text-white min-w-[120px]"
            >
              <option value="">الكل</option>
              <option value="scientific">علمي</option>
              <option value="literary">أدبي</option>
            </select>
          </div>

          {(() => {
            const sq = studentSearch.trim().toLowerCase();
            const matchStudent = (s) => {
              if (filterGrade && s.grade !== filterGrade) return false;
              if (filterBranch && s.study_branch !== filterBranch) return false;
              if (!sq) return true;
              const name = [s.firstName, s.middleName, s.lastName].filter(Boolean).join(' ').toLowerCase();
              return name.includes(sq) || (s.phone || '').includes(sq) || (s.governorate || '').toLowerCase().includes(sq) || (s.school || '').toLowerCase().includes(sq);
            };
            const filteredPending = pendingStudents.filter(matchStudent);
            const filteredActive = students.filter(matchStudent);
            const filteredKicked = kickedStudents.filter(matchStudent);
            return (
              <>
                {filteredPending.length > 0 && (
                  <div className="glass-panel p-6 border-2 border-brand-red">
                    <h3 className="font-bold text-brand-red mb-4 text-lg">
                      الطلاب المعلقين (قيد المراجعة) ({filteredPending.length})
                    </h3>
                    <div className="overflow-x-auto">
                      <table className="w-full text-right">
                        <thead className="border-b border-white/10">
                          <tr>
                            <th className="pb-3 text-slate-400">الاسم</th>
                            <th className="pb-3 text-slate-400">رقم الهاتف</th>
                            <th className="pb-3 text-slate-400">الصف الدراسي</th>
                            <th className="pb-3 text-slate-400">الشعبة</th>
                            <th className="pb-3 text-slate-400">المحافظة</th>
                            <th className="pb-3 text-slate-400">المدرسة</th>
                            <th className="pb-3 text-slate-400">موبايل الأب</th>
                            <th className="pb-3 text-slate-400">موبايل الأم</th>
                            <th className="pb-3 text-slate-400 text-center">إجراءات</th>
                          </tr>
                        </thead>
                        <tbody>
                          {filteredPending.map(student => (
                            <tr key={student.id} className="border-b border-white/5 hover:bg-white/5 transition">
                              <td className="py-4 font-bold">{[student.firstName, student.middleName, student.lastName].filter(Boolean).join(' ')}</td>
                              <td className="py-4">{student.phone}</td>
                              <td className="py-4">
                                <span className="bg-brand-red/20 text-brand-red px-3 py-1 rounded-full text-xs font-bold">
                                  {student.grade === '1sec' ? 'أولى ثانوي' : student.grade === '2sec' ? 'تانية ثانوي' : 'تالتة ثانوي'}
                                </span>
                              </td>
                              <td className="py-4">
                                <span className="bg-blue-500/20 text-blue-400 px-3 py-1 rounded-full text-xs font-bold flex items-center gap-1 w-fit">
                                  <GraduationCap size={12} />
                                  {student.study_branch === 'scientific' ? 'علمي' : 'أدبي'}
                                </span>
                              </td>
                              <td className="py-4 text-slate-300">{student.governorate || '-'}</td>
                              <td className="py-4 text-slate-300">{student.school || '-'}</td>
                              <td className="py-4 text-slate-300">{student.father_phone || '-'}</td>
                              <td className="py-4 text-slate-300">{student.mother_phone || '-'}</td>
                              <td className="py-4 text-center">
                                <div className="flex gap-2 justify-center">
                                  <button
                                    onClick={() => handleAccept(student)}
                                    className="bg-green-500/20 text-green-400 px-4 py-1.5 rounded-lg hover:bg-green-500 hover:text-white transition font-bold text-sm"
                                  >
                                    قبول
                                  </button>
                                  <button
                                    onClick={() => handleReject(student)}
                                    className="bg-red-500/20 text-red-500 px-4 py-1.5 rounded-lg hover:bg-red-500 hover:text-white transition font-bold text-sm"
                                  >
                                    رفض وحذف
                                  </button>
                                  <button
                                    onClick={() => {
                                      setMessageTarget(student);
                                      setShowMessageModal(true);
                                    }}
                                    className="bg-white/10 text-white px-4 py-1.5 rounded-lg hover:bg-white/20 transition font-bold text-sm"
                                  >
                                    رسالة
                                  </button>
                                </div>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                )}

                <div className="glass-panel p-6">
                  <div className="flex justify-between items-center mb-4">
                    <h3 className="font-bold text-lg">الطلاب النشطين ({filteredActive.length})</h3>
                    <button
                      onClick={handleExportStudentsToExcel}
                      className="flex items-center gap-2 bg-green-600 hover:bg-green-700 text-white px-4 py-2 rounded-lg font-bold transition-colors"
                      disabled={filteredActive.length === 0}
                    >
                      <FileSpreadsheet size={18} />
                      تصدير بيانات الطلاب
                    </button>
                  </div>
                  <div className="overflow-x-auto">
                    <table className="w-full text-right">
                      <thead className="border-b border-white/10">
                        <tr>
                          <th className="pb-3 text-slate-400">الاسم</th>
                          <th className="pb-3 text-slate-400">رقم الهاتف</th>
                          <th className="pb-3 text-slate-400">الصف الدراسي</th>
                          <th className="pb-3 text-slate-400">الشعبة</th>
                          <th className="pb-3 text-slate-400">المحافظة</th>
                          <th className="pb-3 text-slate-400">المدرسة</th>
                          <th className="pb-3 text-slate-400">موبايل الأب</th>
                          <th className="pb-3 text-slate-400">موبايل الأم</th>
                          <th className="pb-3 text-slate-400">Bio</th>
                          <th className="pb-3 text-slate-400 text-center">الحالة</th>
                          <th className="pb-3 text-slate-400 text-center">رسالة</th>
                        </tr>
                      </thead>
                      <tbody>
                        {filteredActive.map(student => (
                          <tr key={student.id} className="border-b border-white/5 hover:bg-white/5 transition">
                            <td className="py-4">
                              <div className="flex items-center gap-2">
                                <span className="font-bold">{[student.firstName, student.middleName, student.lastName].filter(Boolean).join(' ')}</span>
                                {student.is_trial === 1 && (
                                  <span className="bg-yellow-500/20 text-yellow-400 px-2 py-0.5 rounded-full text-xs font-bold border border-yellow-500/30">
                                    تجريبي
                                  </span>
                                )}
                              </div>
                            </td>
                            <td className="py-4">{student.phone}</td>
                            <td className="py-4">
                              <div className="flex items-center gap-1">
                                <span className="bg-white/10 text-slate-300 px-3 py-1 rounded-full text-xs font-bold">
                                  {student.grade === '1sec' ? 'أولى ثانوي' : student.grade === '2sec' ? 'تانية ثانوي' : 'تالتة ثانوي'}
                                </span>
                                <button
                                  onClick={() => {
                                    setGradeTarget(student);
                                    setSelectedGrade(student.grade || '');
                                    setShowGradeModal(true);
                                  }}
                                  className="text-amber-400 hover:text-amber-300 p-1 rounded transition"
                                  title="تغيير الصف"
                                >
                                  <Edit3 size={14} />
                                </button>
                              </div>
                            </td>
                            <td className="py-4">
                              <span className="bg-blue-500/20 text-blue-400 px-3 py-1 rounded-full text-xs font-bold flex items-center gap-1 w-fit">
                                <GraduationCap size={12} />
                                {student.study_branch === 'scientific' ? 'علمي' : 'أدبي'}
                              </span>
                            </td>
                            <td className="py-4 text-slate-300">{student.governorate || '-'}</td>
                            <td className="py-4 text-slate-300">{student.school || '-'}</td>
                            <td className="py-4 text-slate-300">{student.father_phone || '-'}</td>
                            <td className="py-4 text-slate-300">{student.mother_phone || '-'}</td>
                            <td className="py-4 text-slate-300 max-w-[200px]">
                              <span className="block line-clamp-2">{student.bio || '-'}</span>
                            </td>
                            <td className="py-4 text-center">
                              <div className="flex gap-2 justify-center flex-wrap">
                                <span className="bg-green-500/20 text-green-400 px-3 py-1 rounded-full text-xs font-bold">نشط ✓</span>
                                <button
                                  onClick={() => handleKick(student)}
                                  className="bg-red-500/20 text-red-400 px-3 py-1.5 rounded-lg hover:bg-red-500 hover:text-white transition font-bold text-xs"
                                  title="طرد الطالب نهائياً"
                                >
                                  طرد
                                </button>
                                <button
                                  onClick={() => handleManualSubscription(student)}
                                  className="bg-blue-500/20 text-blue-400 px-3 py-1.5 rounded-lg hover:bg-blue-500 hover:text-white transition font-bold text-xs"
                                  title="تفعيل اشتراك يدوي"
                                >
                                  تفعيل اشتراك
                                </button>
                                <button
                                  onClick={() => handleToggleLeaderboard(student)}
                                  disabled={loadingLeaderboardId === student.id}
                                  className={`px-3 py-1.5 rounded-lg font-bold text-xs transition flex items-center gap-1 ${student.is_leaderboard === 1 ? 'bg-yellow-500/30 text-yellow-400 hover:bg-yellow-500 hover:text-white' : 'bg-white/10 text-slate-400 hover:bg-yellow-500/20 hover:text-yellow-400'}`}
                                  title={student.is_leaderboard === 1 ? 'إزالة من لوحة الشرف' : 'إضافة للوحة الشرف'}
                                >
                                  <Star size={14} fill={student.is_leaderboard === 1 ? 'currentColor' : 'none'} />
                                  {loadingLeaderboardId === student.id ? '...' : (student.is_leaderboard === 1 ? 'في الشرف' : 'شرف')}
                                </button>
                                <button
                                  onClick={() => handleAddPoints(student)}
                                  className="bg-emerald-500/20 text-emerald-400 px-3 py-1.5 rounded-lg hover:bg-emerald-500 hover:text-white transition font-bold text-xs"
                                  title="إضافة نقاط التميز"
                                >
                                  نقاط+
                                </button>
                                <button
                                  onClick={() => handleDeductPoints(student)}
                                  className="bg-red-500/20 text-red-400 px-3 py-1.5 rounded-lg hover:bg-red-500 hover:text-white transition font-bold text-xs"
                                  title="خصم نقاط من الطالب"
                                >
                                  خصم-
                                </button>
                                <button
                                  onClick={() => handleToggleForumBlock(student)}
                                  className={`px-3 py-1.5 rounded-lg font-bold text-xs transition ${student.is_blocked_from_forum === 1 ? 'bg-orange-500/20 text-orange-400 hover:bg-orange-500 hover:text-white' : 'bg-slate-500/20 text-slate-400 hover:bg-slate-500 hover:text-white'}`}
                                  title={student.is_blocked_from_forum === 1 ? 'إلغاء حظر المنتدى' : 'حظر من المنتدى'}
                                >
                                  {student.is_blocked_from_forum === 1 ? 'محظور 🚫' : 'حظر منتدى'}
                                </button>
                                <button
                                  onClick={() => handleToggleMirror(student)}
                                  className={`px-3 py-1.5 rounded-lg font-bold text-xs transition flex items-center gap-1 ${student.ui_mirror === 1 ? 'bg-cyan-500/30 text-cyan-300 hover:bg-cyan-500 hover:text-white' : 'bg-white/10 text-slate-400 hover:bg-cyan-500/20 hover:text-cyan-300'}`}
                                  title={student.ui_mirror === 1 ? 'إلغاء عكس الأزرار لهذا الطالب' : 'عكس الأزرار لهذا الطالب (لو زرار الجودة مش شغال عنده)'}
                                >
                                  <ArrowLeftRight size={14} />
                                  {student.ui_mirror === 1 ? 'معكوس ⇄' : 'عكس الأزرار'}
                                </button>
                              </div>
                            </td>
                            <td className="py-4 text-center">
                              <div className="flex gap-2 justify-center">
                                <button
                                  onClick={() => {
                                    setMessageTarget(student);
                                    setShowMessageModal(true);
                                  }}
                                  className="bg-white/10 hover:bg-white/20 px-3 py-1.5 rounded-lg font-bold text-sm transition"
                                >
                                  رسالة
                                </button>
                                <button
                                  onClick={async () => {
                                    const newPassword = window.prompt(`أدخل كلمة المرور الجديدة للطالب ${student.firstName}:`);
                                    if (!newPassword || !newPassword.trim()) return;
                                    const res = await d1Api.resetUserPassword(student.id, newPassword.trim());
                                    if (res.success) {
                                      alert('✅ تم تغيير كلمة المرور بنجاح');
                                    } else {
                                      alert('❌ فشل تغيير كلمة المرور: ' + (res.error || 'Unknown'));
                                    }
                                  }}
                                  className="bg-purple-500/20 hover:bg-purple-500/30 text-purple-400 px-3 py-1.5 rounded-lg font-bold text-sm transition"
                                  title="تغيير كلمة المرور"
                                >
                                  تغيير الباسورد
                                </button>
                                <button
                                  onClick={() => handleResetDevice(student)}
                                  className="bg-blue-500/20 text-blue-400 hover:bg-blue-500 hover:text-white px-3 py-1.5 rounded-lg text-sm font-bold transition"
                                  title="فك ارتباط الجهاز"
                                >
                                  📱 فك الجهاز
                                </button>
                                <button
                                  onClick={() => handleToggleTrial(student)}
                                  className={`px-3 py-1.5 rounded-lg font-bold text-xs transition ${
                                    student.is_trial === 1
                                      ? 'bg-green-500/20 text-green-400 hover:bg-green-500 hover:text-white'
                                      : 'bg-yellow-500/20 text-yellow-400 hover:bg-yellow-500 hover:text-white'
                                  }`}
                                  title={student.is_trial === 1 ? 'تحويل لحساب أساسي' : 'تحويل لحساب تجريبي'}
                                >
                                  {student.is_trial === 1 ? 'أساسي' : 'تجريبي'}
                                </button>
                              </div>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>

                {/* Kicked / Expelled Students */}
                {filteredKicked.length > 0 && (
                  <div className="glass-panel p-6 border-2 border-orange-500/30">
                    <h3 className="font-bold text-orange-400 mb-4 text-lg flex items-center gap-2">
                      <XCircle size={20} />
                      الطلاب المطرودون ({filteredKicked.length})
                    </h3>
                    <div className="overflow-x-auto">
                      <table className="w-full text-right">
                        <thead className="border-b border-white/10">
                          <tr>
                            <th className="pb-3 text-slate-400">الاسم</th>
                            <th className="pb-3 text-slate-400">رقم الهاتف</th>
                            <th className="pb-3 text-slate-400">الصف الدراسي</th>
                            <th className="pb-3 text-slate-400">الشعبة</th>
                            <th className="pb-3 text-slate-400">المحافظة</th>
                            <th className="pb-3 text-slate-400">المدرسة</th>
                            <th className="pb-3 text-slate-400 text-center">إجراءات</th>
                          </tr>
                        </thead>
                        <tbody>
                          {filteredKicked.map(student => (
                            <tr key={student.id} className="border-b border-white/5 hover:bg-white/5 transition">
                              <td className="py-4 font-bold text-orange-300/80">{[student.firstName, student.middleName, student.lastName].filter(Boolean).join(' ')}</td>
                              <td className="py-4">{student.phone}</td>
                              <td className="py-4">
                                <span className="bg-white/10 text-slate-300 px-3 py-1 rounded-full text-xs font-bold">
                                  {student.grade === '1sec' ? 'أولى ثانوي' : student.grade === '2sec' ? 'تانية ثانوي' : 'تالتة ثانوي'}
                                </span>
                              </td>
                              <td className="py-4">
                                <span className="bg-blue-500/20 text-blue-400 px-3 py-1 rounded-full text-xs font-bold flex items-center gap-1 w-fit">
                                  <GraduationCap size={12} />
                                  {student.study_branch === 'scientific' ? 'علمي' : 'أدبي'}
                                </span>
                              </td>
                              <td className="py-4 text-slate-300">{student.governorate || '-'}</td>
                              <td className="py-4 text-slate-300">{student.school || '-'}</td>
                              <td className="py-4 text-center">
                                <div className="flex gap-2 justify-center">
                                  <span className="bg-orange-500/20 text-orange-400 px-3 py-1 rounded-full text-xs font-bold">مطرود ✗</span>
                                  <button
                                    onClick={() => handleRestore(student)}
                                    className="bg-green-500/20 text-green-400 px-3 py-1.5 rounded-lg hover:bg-green-500 hover:text-white transition font-bold text-xs"
                                    title="إعادة الطالب للمنصة"
                                  >
                                    استعادة
                                  </button>
                                  <button
                                    onClick={() => {
                                      if (confirm(`حذف الطالب "${student.firstName} ${student.lastName}" نهائياً من قاعدة البيانات؟`)) {
                                        d1Api.deleteUser(student.id).then(res => {
                                          if (res.success) setKickedStudents(prev => prev.filter(s => s.id !== student.id));
                                          else alert('حدث خطأ: ' + (res.error || 'لم يتم الحذف'));
                                        });
                                      }
                                    }}
                                    className="bg-red-500/20 text-red-400 px-3 py-1.5 rounded-lg hover:bg-red-500 hover:text-white transition font-bold text-xs"
                                    title="حذف نهائي من قاعدة البيانات"
                                  >
                                    حذف نهائي
                                  </button>
                                </div>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                )}
              </>
            );
          })()}
        </div>
      )}

      {activeTab === 'announcements' && (        <div className="space-y-6">
          <h2 className="text-2xl font-bold mb-4 flex items-center gap-2">
            <Bell size={24} />
            إدارة الإعلانات
          </h2>

          <div className="glass-panel p-6 border-2 border-brand-red/30 bg-gradient-to-b from-brand-red/10 to-transparent">
            <h3 className="font-bold text-lg mb-4">إضافة إعلان جديد</h3>
            <form onSubmit={handleCreateAnnouncement} className="space-y-4">
              <input
                type="text"
                placeholder="عنوان الإعلان"
                value={newAnnouncement.title}
                onChange={(e) => setNewAnnouncement({ ...newAnnouncement, title: e.target.value })}
                className="w-full p-4 bg-white/5 border border-white/10 rounded-xl focus:border-brand-gold outline-none"
                required
              />
              <textarea
                placeholder="محتوى الإعلان..."
                value={newAnnouncement.content}
                onChange={(e) => setNewAnnouncement({ ...newAnnouncement, content: e.target.value })}
                className="w-full p-4 bg-white/5 border border-white/10 rounded-xl focus:border-brand-gold outline-none h-32"
                required
              ></textarea>
              <button type="submit" className="btn-main px-8 py-3 rounded-xl font-bold hover:scale-105 transition">
                إرسال الإعلان للجميع
              </button>
            </form>
          </div>

          <div className="space-y-4 mt-8">
            <h3 className="font-bold text-lg">الإعلانات السابقة ({announcements.length})</h3>
            {announcements.length === 0 ? (
              <p className="text-slate-400 text-center py-8">لا توجد إعلانات حالياً</p>
            ) : (
              announcements.map((announcement) => (
                <div key={announcement.id} className="glass-panel p-6 relative">
                  <button
                    onClick={() => handleDeleteAnnouncement(announcement.id)}
                    className="absolute top-4 left-4 text-red-400 hover:text-red-300 bg-red-400/10 p-2 rounded-lg transition"
                  >
                    <Trash2 size={18} />
                  </button>
                  <h4 className="font-bold text-xl text-brand-gold mb-2">{announcement.title}</h4>
                  <p className="text-slate-300 leading-relaxed max-w-[90%]">{announcement.content}</p>
                  <p className="text-xs text-slate-500 mt-4 text-left">
                    {new Date(announcement.created_at).toLocaleDateString('ar-EG')}
                  </p>
                </div>
              ))
            )}
          </div>
        </div>
      )}

      {activeTab === 'shield' && <ShieldSettings />}

      {/* Add Course Modal */}
      {showAddCourse && (
        <div className="fixed inset-0 bg-black/90 z-[100] flex items-center justify-center p-4" onClick={() => setShowAddCourse(false)}>
          <div className="glass-panel p-8 max-w-2xl w-full" onClick={e => e.stopPropagation()}>
            <h3 className="text-2xl font-bold mb-6">إضافة كورس جديد</h3>

            <div className="space-y-4">
              <input
                placeholder="عنوان الكورس"
                value={newCourse.title}
                onChange={(e) => setNewCourse({ ...newCourse, title: e.target.value })}
                className="w-full p-4 bg-white/5 border border-white/10 rounded-xl"
              />

              <textarea
                placeholder="وصف الكورس"
                value={newCourse.description}
                onChange={(e) => setNewCourse({ ...newCourse, description: e.target.value })}
                className="w-full p-4 bg-white/5 border border-white/10 rounded-xl h-24"
              />

              <div className="grid md:grid-cols-2 gap-4">
                <input
                  type="number"
                  placeholder="السعر"
                  value={newCourse.price}
                  onChange={(e) => setNewCourse({ ...newCourse, price: parseInt(e.target.value) })}
                  className="p-4 bg-white/5 border border-white/10 rounded-xl"
                />

                <input
                  type="number"
                  placeholder="المدة (يوم)"
                  value={newCourse.duration_days}
                  onChange={(e) => setNewCourse({ ...newCourse, duration_days: parseInt(e.target.value) })}
                  className="p-4 bg-white/5 border border-white/10 rounded-xl"
                />
              </div>

              <select
                value={newCourse.grade}
                onChange={(e) => setNewCourse({ ...newCourse, grade: e.target.value })}
                className="w-full p-4 bg-white/5 border border-white/10 rounded-xl"
              >
                <option value="1sec">أولى ثانوي</option>
                <option value="2sec">تانية ثانوي</option>
                <option value="3sec">تالتة ثانوي</option>
              </select>

              <div className="flex flex-col gap-2">
                <label className="text-sm font-bold text-slate-300">صورة الكورس (رفع مباشر ImgBB):</label>
                <input type="file" accept="image/*" onChange={(e) => setSelectedCourseImage(e.target.files[0])} className="p-2 bg-white/5 border border-white/10 rounded-xl text-white" />
              </div>

              <input
                placeholder="أو الصق رابط الصورة مباشرة"
                value={newCourse.image_url}
                onChange={(e) => setNewCourse({ ...newCourse, image_url: e.target.value })}
                className="w-full p-4 bg-white/5 border border-white/10 rounded-xl"
              />

              <div className="flex flex-col gap-2">
                <label className="text-sm font-bold text-slate-300">حالة الكورس:</label>
                <select
                  value={newCourse.status}
                  onChange={(e) => setNewCourse({ ...newCourse, status: e.target.value })}
                  className="w-full p-4 bg-white/5 border border-white/10 rounded-xl text-white"
                >
                  <option value="active">✅ منشور - متاح للجميع</option>
                  <option value="locked">🔒 مقفول - يظهر بعلامة قفل ولا يمكن شراؤه</option>
                  <option value="hidden">👁️ مخفي - لا يظهر للطلاب إطلاقاً</option>
                </select>
              </div>

              {/* Fixed Expiration Date */}
              <div className="flex flex-col gap-2 p-4 bg-amber-500/5 border border-amber-500/20 rounded-xl">
                <label className="text-sm font-bold text-amber-300">📅 تاريخ انتهاء ثابت (اختياري):</label>
                <input
                  type="date"
                  value={newCourse.fixed_expiration_date || ''}
                  onChange={(e) => setNewCourse({ ...newCourse, fixed_expiration_date: e.target.value })}
                  className="w-full p-4 bg-white/5 border border-white/10 rounded-xl text-white"
                />
                <p className="text-xs text-slate-400">
                  ℹ️ عند تعيين تاريخ ثابت، ينتهي الكورس للجميع في نفس اللحظة بغض النظر عن تاريخ اشتراكهم. اتركه فارغاً لاستخدام المدة الفردية لكل اشتراك.
                </p>
              </div>

              <div className="flex gap-3 pt-4">
                <button
                  onClick={() => setShowAddCourse(false)}
                  className="flex-1 py-4 bg-white/10 rounded-xl font-bold"
                >
                  إلغاء
                </button>
                <button
                  onClick={handleAddCourse}
                  disabled={isUploading}
                  className="flex-1 btn-main py-4 rounded-xl font-bold disabled:opacity-50"
                >
                  {isUploading ? 'جاري رفع الصورة...' : 'إضافة الكورس'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {showAddLecture && (
        <div className="fixed inset-0 bg-black/90 z-[100] flex items-start justify-center p-4 overflow-y-auto" onClick={() => { setShowAddLecture(false); setEditMode(false); setEditingLectureId(null); }}>
          <div className="glass-panel p-8 max-w-2xl w-full my-4 max-h-[92vh] overflow-y-auto" style={{ scrollbarWidth: 'thin' }} onClick={e => e.stopPropagation()}>
            <h3 className="text-2xl font-bold mb-3">{editMode ? '✏️ تعديل المحاضرة' : 'إضافة محاضرة جديدة'}</h3>
            <p className="mb-6 p-3 rounded-xl bg-red-500/10 border border-red-500/40 text-red-300 font-bold text-sm">
              تنبيه: تأكد من صحة البيانات (الروابط والأسماء) قبل الإضافة؛ لا يمكن تعديل المحاضرة بعد حفظها، سيتعين عليك حذفها وإعادتها.
            </p>

            <div className="space-y-4">
              <input
                placeholder="عنوان المحاضرة"
                value={newLecture.title}
                onChange={(e) => setNewLecture({ ...newLecture, title: e.target.value })}
                className="w-full p-4 bg-white/5 border border-white/10 rounded-xl"
              />

              {/* Videos List Builder */}
              <div className="space-y-2 border border-white/10 p-4 rounded-xl bg-black/20">
                <div className="flex justify-between items-center mb-2">
                  <span className="font-bold text-slate-300">أجزاء الفيديو لهذه المحاضرة</span>
                  <button
                    onClick={() => setNewLecture({ ...newLecture, videos_list: [...newLecture.videos_list, { title: '', url: '' }] })}
                    className="text-xs bg-brand-gold text-black px-3 py-1 rounded-lg font-bold hover:bg-yellow-400"
                  >
                    + إضافة فيديو
                  </button>
                </div>
                {newLecture.videos_list.map((video, idx) => (
                  <div key={idx} className="flex gap-2">
                    <input
                      placeholder="عنوان الجزء (مثال: الجزء الأول)"
                      value={video.title}
                      onChange={(e) => {
                        const newVids = [...newLecture.videos_list];
                        newVids[idx].title = e.target.value;
                        setNewLecture({ ...newLecture, videos_list: newVids });
                      }}
                      className="w-1/3 p-3 bg-white/5 border border-white/10 rounded-xl text-sm"
                    />
                    <input
                      placeholder="رابط الفيديو (يوتيوب أو m3u8)"
                      value={video.url}
                      onChange={(e) => {
                        const newVids = [...newLecture.videos_list];
                        newVids[idx].url = e.target.value;
                        setNewLecture({ ...newLecture, videos_list: newVids });
                      }}
                      className="flex-1 p-3 bg-white/5 border border-white/10 rounded-xl text-sm"
                    />
                    <button
                      onClick={() => {
                        const newVids = newLecture.videos_list.filter((_, i) => i !== idx);
                        setNewLecture({ ...newLecture, videos_list: newVids });
                      }}
                      className="bg-red-500/20 text-red-500 px-3 rounded-xl hover:bg-red-500 hover:text-white transition"
                    >
                      ✕
                    </button>
                  </div>
                ))}
                {newLecture.videos_list.length === 0 && <p className="text-xs text-slate-500 text-center py-2">لم تقم بإضافة فيديوهات بعد</p>}
              </div>

              {/* PDFs List Builder */}
              <div className="space-y-2 border border-white/10 p-4 rounded-xl bg-black/20">
                <div className="flex justify-between items-center mb-2">
                  <span className="font-bold text-slate-300">المذكرات (PDFs) لهذه المحاضرة</span>
                  <button
                    onClick={() => setNewLecture({ ...newLecture, pdfs_list: [...newLecture.pdfs_list, { title: '', url: '' }] })}
                    className="text-xs bg-brand-red text-white px-3 py-1 rounded-lg font-bold hover:bg-red-600"
                  >
                    + إضافة مذكرة
                  </button>
                </div>
                {newLecture.pdfs_list.map((pdf, idx) => (
                  <div key={idx} className="flex gap-2">
                    <input
                      placeholder="عنوان المذكرة"
                      value={pdf.title}
                      onChange={(e) => {
                        const newPdfs = [...newLecture.pdfs_list];
                        newPdfs[idx].title = e.target.value;
                        setNewLecture({ ...newLecture, pdfs_list: newPdfs });
                      }}
                      className="w-1/3 p-3 bg-white/5 border border-white/10 rounded-xl text-sm"
                    />
                    <input
                      placeholder="رابط التحميل (درايف الخ)"
                      value={pdf.url}
                      onChange={(e) => {
                        const newPdfs = [...newLecture.pdfs_list];
                        newPdfs[idx].url = e.target.value;
                        setNewLecture({ ...newLecture, pdfs_list: newPdfs });
                      }}
                      className="flex-1 p-3 bg-white/5 border border-white/10 rounded-xl text-sm"
                    />
                    <button
                      onClick={() => {
                        const newPdfs = newLecture.pdfs_list.filter((_, i) => i !== idx);
                        setNewLecture({ ...newLecture, pdfs_list: newPdfs });
                      }}
                      className="bg-red-500/20 text-red-500 px-3 rounded-xl hover:bg-red-500 hover:text-white transition"
                    >
                      ✕
                    </button>
                  </div>
                ))}
                {newLecture.pdfs_list.length === 0 && <p className="text-xs text-slate-500 text-center py-2">لم تقم بإضافة مذكرات بعد</p>}
              </div>

              <textarea
                placeholder="وصف المحاضرة"
                value={newLecture.description}
                onChange={(e) => setNewLecture({ ...newLecture, description: e.target.value })}
                className="w-full p-4 bg-white/5 border border-white/10 rounded-xl h-24"
              />

              <select
                value={newLecture.course_id}
                onChange={(e) => setNewLecture({ ...newLecture, course_id: e.target.value })}
                className="w-full p-4 bg-white/5 border border-white/10 rounded-xl"
              >
                <option value="">-- اختر كورس --</option>
                {courses?.length > 0 && courses.map((course) => (
                  <option key={course.id} value={course.id}>
                    {course.title}
                  </option>
                ))}
              </select>

              <div className="grid md:grid-cols-2 gap-4">
                <input
                  type="number"
                  placeholder="المدة (دقيقة)"
                  value={newLecture.duration_minutes}
                  onChange={(e) => setNewLecture({ ...newLecture, duration_minutes: parseInt(e.target.value) })}
                  className="p-4 bg-white/5 border border-white/10 rounded-xl"
                />

                <input
                  type="number"
                  placeholder="الترتيب"
                  value={newLecture.order_index}
                  onChange={(e) => setNewLecture({ ...newLecture, order_index: parseInt(e.target.value) })}
                  className="p-4 bg-white/5 border border-white/10 rounded-xl"
                />
              </div>

              <div className="grid md:grid-cols-2 gap-4">
                <div className="flex flex-col gap-2">
                  <label className="text-sm font-bold text-slate-300">صورة المحاضرة المصغرة (رفع مباشر ImgBB):</label>
                  <input type="file" accept="image/*" onChange={(e) => setSelectedLectureImage(e.target.files[0])} className="p-2 bg-white/5 border border-white/10 rounded-xl text-white" />
                </div>
                <input
                  placeholder="أو الصق رابط الصورة المصغرة مباشرة"
                  value={newLecture.thumbnail_url || ''}
                  onChange={(e) => setNewLecture({ ...newLecture, thumbnail_url: e.target.value })}
                  className="w-full p-4 bg-white/5 border border-white/10 rounded-xl"
                />
              </div>

              <div className="grid md:grid-cols-2 gap-4">
                <input
                  placeholder="رابط خارجي (اختياري)"
                  value={newLecture.external_link || ''}
                  onChange={(e) => setNewLecture({ ...newLecture, external_link: e.target.value })}
                  className="w-full p-4 bg-white/5 border border-white/10 rounded-xl"
                />

                <input
                  placeholder="رابط الواجب الخارجي"
                  value={newLecture.homework_url || ''}
                  onChange={(e) => setNewLecture({ ...newLecture, homework_url: e.target.value })}
                  className="w-full p-4 bg-white/5 border border-white/10 rounded-xl"
                />
              </div>

              {/* إضافة خيار القفل الإجباري في لوحة الأدمن */}
              <div className="space-y-4 p-4 bg-white/5 rounded-2xl border border-white/10">
                <label className="flex items-center gap-3 cursor-pointer group">
                  <input 
                    type="checkbox" 
                    checked={newLecture.is_quiz_required || false} 
                    onChange={(e) => {
                      console.log('is_quiz_required changed:', e.target.checked);
                      setNewLecture({ ...newLecture, is_quiz_required: e.target.checked });
                    }}
                    className="w-5 h-5 accent-brand-red rounded"
                  />
                  <div className="flex flex-col">
                    <span className="font-bold text-white group-hover:text-brand-red transition">قفل المحاضرة بشيت إجباري 🔒</span>
                    <span className="text-xs text-slate-400">إذا فعلت هذا الخيار، لن تفتح المحاضرة للطالب إلا بعد حل الامتحان المرفق.</span>
                  </div>
                </label>

                <div className="flex flex-col gap-2">
                  <label className="text-sm font-bold text-slate-300">اختر الامتحان المرتبط (الشيت):</label>
                  <select
                    value={newLecture.required_quiz_id || ''}
                    onChange={(e) => {
                      console.log('required_quiz_id changed:', e.target.value);
                      setNewLecture({ ...newLecture, required_quiz_id: e.target.value || null });
                    }}
                    className="w-full p-4 bg-black/40 border border-white/10 rounded-xl text-white outline-none focus:border-brand-red"
                  >
                    <option value="">-- بدون امتحان --</option>
                    {quizzes?.map((quiz) => (
                      <option key={quiz.id} value={quiz.id}>{quiz.title}</option>
                    ))}
                  </select>
                </div>
              </div>

              {/* Multiple Quiz Selection - امتحانات إضافية للمذاكرة */}
              <div className="space-y-2 p-4 bg-white/5 rounded-2xl border border-white/10">
                <label className="text-sm font-bold text-slate-300 block">📝 امتحانات إضافية للمذاكرة (اختياري):</label>
                <p className="text-xs text-slate-500 mb-2">هذه الامتحانات ستظهر تحت المحاضرة للمذاكرة فقط ولن تقفل المحاضرة</p>
                <div className="max-h-32 overflow-y-auto bg-black/40 border border-white/10 rounded-xl p-2 space-y-1">
                  {quizzes?.length === 0 ? (
                    <p className="text-xs text-slate-500 text-center py-2">لا توجد امتحانات</p>
                  ) : (
                    quizzes?.map((quiz) => {
                      const quizIds = newLecture.quiz_ids || [];
                      const isSelected = quizIds.includes(quiz.id);
                      // Don't show the required quiz in the additional list
                      if (newLecture.required_quiz_id && quiz.id === newLecture.required_quiz_id) return null;
                      return (
                        <label key={quiz.id} className="flex items-center gap-2 p-2 rounded-lg hover:bg-white/10 cursor-pointer transition">
                          <input
                            type="checkbox"
                            checked={isSelected}
                            onChange={(e) => {
                              const currentQuizIds = [...(newLecture.quiz_ids || [])];
                              if (e.target.checked) {
                                if (!currentQuizIds.includes(quiz.id)) currentQuizIds.push(quiz.id);
                              } else {
                                const idx = currentQuizIds.indexOf(quiz.id);
                                if (idx > -1) currentQuizIds.splice(idx, 1);
                              }
                              setNewLecture({ ...newLecture, quiz_ids: currentQuizIds });
                            }}
                            className="w-4 h-4 rounded accent-amber-500"
                          />
                          <span className="text-sm text-white">{quiz.title}</span>
                        </label>
                      );
                    })
                  )}
                </div>
              </div>

              <label className="flex items-center gap-2">
                <input
                  type="checkbox"
                  checked={newLecture.is_free}
                  onChange={(e) => setNewLecture({ ...newLecture, is_free: e.target.checked })}
                />
                محاضرة مجانية
              </label>

              <label className="flex items-center gap-3 cursor-pointer group p-3 bg-cyan-500/5 border border-cyan-500/20 rounded-xl">
                <input
                  type="checkbox"
                  checked={newLecture.is_preview || false}
                  onChange={(e) => setNewLecture({ ...newLecture, is_preview: e.target.checked })}
                  className="w-5 h-5 accent-cyan-500 rounded"
                />
                <div className="flex flex-col">
                  <span className="font-bold text-white group-hover:text-cyan-400 transition">إتاحة للمعاينة المجانية (Preview) 👁️</span>
                  <span className="text-xs text-slate-400">الطلاب غير المشتركين سيتمكنون من مشاهدة هذه المحاضرة بالكامل كمعاينة مجانية.</span>
                </div>
              </label>

              <div className="flex gap-3 pt-4">
                <button
                  onClick={() => setShowAddLecture(false)}
                  className="flex-1 py-4 bg-white/10 rounded-xl font-bold"
                >
                  إلغاء
                </button>
                <button
                  onClick={handleSaveLecture}
                  disabled={isUploading}
                  className="flex-1 btn-main py-4 rounded-xl font-bold disabled:opacity-50"
                >
                  {isUploading ? 'جاري رفع الصورة...' : 'إضافة المحاضرة'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Quiz Course Selection Modal */}
      {showQuizCourseSelect && (
        <div className="fixed inset-0 bg-black/90 z-[100] flex items-center justify-center p-4" onClick={() => setShowQuizCourseSelect(false)}>
          <div className="glass-panel p-8 max-w-lg w-full" onClick={e => e.stopPropagation()}>
            <h3 className="text-2xl font-bold mb-6 text-center text-white">اختر الكورس الخاص بالامتحان</h3>
            <div className="space-y-3 max-h-96 overflow-y-auto pr-2 custom-scrollbar">
              {courses.length === 0 ? (
                <p className="text-center text-slate-400 py-4">لاتوجد كورسات متاحة</p>
              ) : (
                courses.map(course => (
                  <button
                    key={course.id}
                    onClick={() => {
                      setSelectedCourseForQuiz(course);
                      setShowQuizCourseSelect(false);
                      setShowQuizBuilder(true);
                    }}
                    className="w-full p-4 bg-white/5 hover:bg-white/10 border border-white/10 rounded-xl text-right transition group flex justify-between items-center"
                  >
                    <div>
                      <h4 className="font-bold text-lg text-brand-gold group-hover:text-white transition">{course.title}</h4>
                      <p className="text-sm text-slate-400 mt-1">
                        {course.grade === '1sec' ? 'أولى ثانوي' : course.grade === '2sec' ? 'تانية ثانوي' : 'تالتة ثانوي'}
                      </p>
                    </div>
                    <ChevronLeft size={20} className="text-slate-500 group-hover:text-brand-gold transition" />
                  </button>
                ))
              )}
            </div>
            <button
              onClick={() => setShowQuizCourseSelect(false)}
              className="w-full mt-6 py-3 bg-white/10 hover:bg-white/20 rounded-xl font-bold transition text-white"
            >
              إلغاء
            </button>
          </div>
        </div>
      )}

      {/* Quiz Builder Modal */}
      {showQuizBuilder && selectedCourseForQuiz && (
        <QuizBuilder
          courseId={selectedCourseForQuiz.id}
          onClose={() => {
            setShowQuizBuilder(false);
            setSelectedCourseForQuiz(null);
            loadData();
          }}
        />
      )}

      {/* Message Modal */}
      {showMessageModal && messageTarget && (
        <div className="fixed inset-0 bg-black/90 z-[200] flex items-center justify-center p-4" onClick={() => setShowMessageModal(false)}>
          <div className="glass-panel p-6 max-w-lg w-full" onClick={e => e.stopPropagation()}>
            <h3 className="text-xl font-bold mb-2">✉️ إرسال رسالة للطالب</h3>
            <p className="text-sm text-slate-400 mb-4">
              إلى: {messageTarget.firstName || 'طالب'} {messageTarget.lastName || ''} ({messageTarget.phone || '-'})
            </p>
            <textarea
              value={messageContent}
              onChange={(e) => setMessageContent(e.target.value)}
              placeholder="اكتب الرسالة هنا..."
              className="w-full p-4 bg-white/5 border border-white/10 rounded-xl focus:border-brand-gold outline-none h-28"
            />
            <div className="flex gap-3 pt-4">
              <button
                onClick={() => {
                  setShowMessageModal(false);
                  setMessageTarget(null);
                  setMessageContent('');
                }}
                className="flex-1 py-3 bg-white/10 rounded-xl font-bold"
              >
                إلغاء
              </button>
              <button
                onClick={handleSendMessage}
                className="flex-1 btn-main py-3 rounded-xl font-bold"
              >
                إرسال
              </button>
            </div>
          </div>
        </div>
      )}
      {/* Subscription Management Modal */}
      {showSubModal && selectedStudentSub && (
        <div className="fixed inset-0 bg-black/90 z-[200] flex items-center justify-center p-4" onClick={() => { setShowSubModal(false); setSelectedStudentSub(null); }}>
          <div className="glass-panel p-6 max-w-lg w-full max-h-[80vh] overflow-y-auto" onClick={e => e.stopPropagation()}>
            <h3 className="text-xl font-bold mb-1 flex items-center gap-2">
              إدارة اشتراكات الطالب
              {selectedStudentSub.is_trial === 1 && (
                <span className="bg-yellow-500/20 text-yellow-400 px-2 py-0.5 rounded-full text-xs font-bold border border-yellow-500/30">
                  حساب تجريبي
                </span>
              )}
            </h3>
            <p className="text-slate-400 text-sm mb-5">
              {[selectedStudentSub.firstName, selectedStudentSub.middleName, selectedStudentSub.lastName].filter(Boolean).join(' ')}
              {selectedStudentSub.phone ? ` — ${selectedStudentSub.phone}` : ''}
            </p>

            {/* Summary Stats */}
            <div className="grid grid-cols-3 gap-3 mb-4">
              <div className="bg-white/5 p-3 rounded-xl border border-white/10 text-center">
                <p className="text-2xl font-black text-white">{studentEnrollments.length}</p>
                <p className="text-xs text-slate-400">إجمالي المشتركين</p>
              </div>
              <div className="bg-green-500/10 p-3 rounded-xl border border-green-500/20 text-center">
                <p className="text-2xl font-black text-green-400">{studentEnrollments.filter(e => !e.is_trial).length}</p>
                <p className="text-xs text-slate-400">حسابات أساسية</p>
              </div>
              <div className="bg-yellow-500/10 p-3 rounded-xl border border-yellow-500/20 text-center">
                <p className="text-2xl font-black text-yellow-400">{studentEnrollments.filter(e => e.is_trial === 1).length}</p>
                <p className="text-xs text-slate-400">حسابات تجريبية</p>
              </div>
            </div>

            {/* Current Enrollments */}
            <h4 className="font-bold mb-3 text-sm text-slate-300">الاشتراكات الحالية:</h4>
            {studentEnrollments.length === 0 ? (
              <p className="text-slate-500 text-sm mb-4">لا توجد اشتراكات حالية</p>
            ) : (
              <div className="space-y-2 mb-4">
                {studentEnrollments.map((enr) => (
                  <div key={enr.enrollment_id} className="flex items-center justify-between bg-white/5 p-3 rounded-xl border border-white/10">
                    <div>
                      <p className="font-bold text-sm">{enr.title || enr.course_id}</p>
                      <p className="text-xs text-slate-400">
                        منذ {enr.enrolled_at ? new Date(Number(enr.enrolled_at)).toLocaleDateString('ar-EG') : '—'}
                        {enr.expires_at ? ` • ينتهي ${new Date(Number(enr.expires_at)).toLocaleDateString('ar-EG')}` : ''}
                      </p>
                    </div>
                    <button
                      onClick={() => handleRevokeSub(enr.enrollment_id)}
                      className="bg-red-500/20 text-red-400 hover:bg-red-500 hover:text-white px-3 py-1.5 rounded-lg text-xs font-bold transition"
                    >
                      سحب الاشتراك
                    </button>
                  </div>
                ))}
              </div>
            )}

            {/* Add New Enrollment */}
            <div className="border-t border-white/10 pt-4 mt-4">
              <h4 className="font-bold mb-3 text-sm text-slate-300">إضافة كورس جديد:</h4>
              <div className="flex gap-2">
                <select
                  value={subCourseId}
                  onChange={(e) => setSubCourseId(e.target.value)}
                  className="flex-1 p-3 bg-white/5 border border-white/10 rounded-xl text-white"
                >
                  <option value="">-- اختر الكورس --</option>
                  {courses.filter(c => !studentEnrollments.some(e => e.course_id === c.id)).map(c => (
                    <option key={c.id} value={c.id}>{c.title}</option>
                  ))}
                </select>
                <button
                  onClick={handleAddSub}
                  disabled={!subCourseId}
                  className="btn-main px-5 py-3 rounded-xl font-bold disabled:opacity-50"
                >
                  إضافة كورس
                </button>
              </div>
            </div>

            <button
              onClick={() => { setShowSubModal(false); setSelectedStudentSub(null); }}
              className="w-full mt-6 py-3 bg-white/10 hover:bg-white/20 rounded-xl font-bold transition text-white"
            >
              إغلاق
            </button>
          </div>
        </div>
      )}

      {/* Grade Change Modal */}
      {showGradeModal && gradeTarget && (
        <div className="fixed inset-0 bg-black/90 z-[200] flex items-center justify-center p-4" onClick={() => { setShowGradeModal(false); setGradeTarget(null); setSelectedGrade(''); }}>
          <div className="glass-panel p-6 max-w-md w-full" onClick={e => e.stopPropagation()}>
            <h3 className="text-xl font-bold mb-2 flex items-center gap-2">
              <GraduationCap size={24} className="text-brand-gold" />
              تغيير الصف الدراسي
            </h3>
            <p className="text-sm text-slate-400 mb-6">
              للطالب: {[gradeTarget.firstName, gradeTarget.middleName, gradeTarget.lastName].filter(Boolean).join(' ')}
            </p>

            <div className="space-y-4">
              <label className="text-sm font-bold text-slate-300 block">اختر الصف الجديد:</label>
              <select
                value={selectedGrade}
                onChange={(e) => setSelectedGrade(e.target.value)}
                className="w-full p-4 bg-white/5 border border-white/10 rounded-xl text-white"
              >
                <option value="1sec">الصف الأول الثانوي (1sec)</option>
                <option value="2sec">الصف الثاني الثانوي (2sec)</option>
                <option value="3sec">الصف الثالث الثانوي (3sec)</option>
              </select>

              <div className="flex gap-3 pt-4">
                <button
                  onClick={() => { setShowGradeModal(false); setGradeTarget(null); setSelectedGrade(''); }}
                  className="flex-1 py-3 bg-white/10 rounded-xl font-bold hover:bg-white/20 transition"
                >
                  إلغاء
                </button>
                <button
                  onClick={() => handleChangeGrade(gradeTarget, selectedGrade)}
                  className="flex-1 btn-main py-3 rounded-xl font-bold"
                >
                  تأكيد التغيير
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </DashboardLayout>
  );
};

export default AdminDashboard;